import React, { useRef, useEffect, useCallback } from 'react';

export const VISUALIZER_STYLES = ['bars', 'wave', 'circular', 'particles', 'oscilloscope', 'spectrum', 'dna'];

export const COLOR_SCHEMES = {
    wave: ['#9333ea', '#ec4899', '#06b6d4', '#8b5cf6'],
    fire: ['#f97316', '#ef4444', '#facc15', '#dc2626'],
    ocean: ['#0ea5e9', '#06b6d4', '#0891b2', '#075985'],
    neon: ['#22d3ee', '#a3e635', '#f472b6', '#facc15'],
    sunset: ['#fb923c', '#f472b6', '#a855f7', '#fbbf24'],
    matrix: ['#22c55e', '#16a34a', '#4ade80', '#052e16'],
    galaxy: ['#6366f1', '#8b5cf6', '#d946ef', '#312e81'],
};

const drawBars = (ctx, W, H, freq, colors, s) => {
    const barCount = 48;
    const gap = 2;
    const barW = Math.max(2, (W - gap * (barCount - 1)) / barCount);
    const grad = ctx.createLinearGradient(0, H, 0, 0);
    grad.addColorStop(0, colors[0]);
    grad.addColorStop(0.55, colors[1]);
    grad.addColorStop(1, colors[2] || colors[1]);
    ctx.fillStyle = grad;
    for (let i = 0; i < barCount; i++) {
        const bin = Math.floor((i / barCount) * freq.length);
        const v = (freq[bin] / 255) * s.sensitivity;
        if (s.mirrorMode) {
            const barH = Math.max(2, v * (H / 2) * 0.92);
            ctx.fillRect(i * (barW + gap), H / 2 - barH, barW, barH);
            ctx.fillRect(i * (barW + gap), H / 2, barW, barH);
        } else {
            const barH = Math.max(2, v * H * 0.92);
            ctx.fillRect(i * (barW + gap), H - barH, barW, barH);
        }
    }
};

const drawWave = (ctx, W, H, time, colors, s) => {
    const drawLine = (color, flip) => {
        ctx.strokeStyle = color;
        ctx.lineWidth = s.lineThickness;
        ctx.beginPath();
        const slice = W / time.length;
        let x = 0;
        for (let i = 0; i < time.length; i++) {
            const v = ((time[i] - 128) / 128) * s.sensitivity * (flip ? -1 : 1);
            const y = H / 2 + v * (H / 2) * 0.9;
            if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
            x += slice;
        }
        ctx.stroke();
    };
    drawLine(colors[0], false);
    if (s.mirrorMode) drawLine(colors[1] || colors[0], true);
};

const drawCircular = (ctx, W, H, freq, colors, s) => {
    const cx = W / 2, cy = H / 2;
    const baseRadius = Math.min(W, H) * 0.22;
    const spokes = 64;
    ctx.lineWidth = s.lineThickness;
    for (let i = 0; i < spokes; i++) {
        const angle = (i / spokes) * Math.PI * 2;
        const bin = Math.floor((i / spokes) * freq.length);
        const v = (freq[bin] / 255) * s.sensitivity;
        const r2 = baseRadius + v * baseRadius * 1.4;
        ctx.strokeStyle = colors[i % colors.length];
        ctx.beginPath();
        ctx.moveTo(cx + Math.cos(angle) * baseRadius, cy + Math.sin(angle) * baseRadius);
        ctx.lineTo(cx + Math.cos(angle) * r2, cy + Math.sin(angle) * r2);
        ctx.stroke();
    }
};

const drawParticles = (ctx, W, H, freq, colors, s, time) => {
    const count = s.particleCount || 50;
    ctx.shadowBlur = 15 * s.glowIntensity;
    for (let i = 0; i < count; i++) {
        const bin = Math.floor((i / count) * freq.length);
        const v = freq[bin] / 255;
        const x = (i / count) * W;
        const y = H / 2 + Math.sin(time * 0.002 + i * 0.3) * H * 0.32 * (0.3 + v * s.sensitivity);
        const size = 2 + v * 6 * s.sensitivity;
        const color = colors[i % colors.length];
        ctx.fillStyle = color;
        ctx.shadowColor = color;
        ctx.beginPath();
        ctx.arc(x, y, size, 0, Math.PI * 2);
        ctx.fill();
    }
    ctx.shadowBlur = 0;
};

const drawOscilloscope = (ctx, W, H, time, colors, s) => {
    ctx.strokeStyle = 'rgba(255,255,255,0.08)';
    ctx.lineWidth = 1;
    for (let gx = 0; gx < W; gx += 20) { ctx.beginPath(); ctx.moveTo(gx, 0); ctx.lineTo(gx, H); ctx.stroke(); }
    for (let gy = 0; gy < H; gy += 20) { ctx.beginPath(); ctx.moveTo(0, gy); ctx.lineTo(W, gy); ctx.stroke(); }

    ctx.strokeStyle = colors[0];
    ctx.lineWidth = s.lineThickness;
    ctx.beginPath();
    const slice = W / time.length;
    let x = 0;
    for (let i = 0; i < time.length; i++) {
        const v = ((time[i] - 128) / 128) * s.sensitivity;
        const y = H / 2 + v * (H / 2) * 0.9;
        if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
        x += slice;
    }
    ctx.stroke();
};

const drawSpectrum = (ctx, W, H, freq, s) => {
    const bars = 64;
    const barW = W / bars;
    for (let i = 0; i < bars; i++) {
        const bin = Math.floor((i / bars) * freq.length);
        const v = (freq[bin] / 255) * s.sensitivity;
        const hue = (i / bars) * 300;
        ctx.fillStyle = `hsl(${hue}, 90%, 55%)`;
        const barH = Math.max(2, v * H * 0.9);
        ctx.fillRect(i * barW, H - barH, barW * 0.85, barH);
    }
};

const drawDNA = (ctx, W, H, freq, colors, s, time) => {
    const amplitude = H * 0.25;
    ctx.lineWidth = s.lineThickness;
    for (let strand = 0; strand < 2; strand++) {
        ctx.strokeStyle = colors[strand % colors.length];
        ctx.beginPath();
        for (let x = 0; x <= W; x += 4) {
            const bin = Math.floor((x / W) * freq.length);
            const v = (freq[bin] / 255) * s.sensitivity;
            const y = H / 2 + Math.sin(x * 0.03 + time * 0.002 + strand * Math.PI) * amplitude * (0.5 + v * 0.5);
            if (x === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
        }
        ctx.stroke();
    }
    ctx.strokeStyle = 'rgba(255,255,255,0.15)';
    ctx.lineWidth = 1;
    for (let x = 0; x <= W; x += 20) {
        const bin = Math.floor((x / W) * freq.length);
        const v = (freq[bin] / 255) * s.sensitivity;
        const y1 = H / 2 + Math.sin(x * 0.03 + time * 0.002) * amplitude * (0.5 + v * 0.5);
        const y2 = H / 2 + Math.sin(x * 0.03 + time * 0.002 + Math.PI) * amplitude * (0.5 + v * 0.5);
        ctx.beginPath();
        ctx.moveTo(x, y1);
        ctx.lineTo(x, y2);
        ctx.stroke();
    }
};

/**
 * Standalone visualizer engine — takes a live Web Audio AnalyserNode (owned
 * and wired by the caller) plus a style/colorScheme/settings selection.
 * Purely presentational: knows nothing about tracks, playback, or gating.
 */
const PlayerVisualizer = ({ analyser, isPlaying, style = 'bars', colorScheme = 'wave', settings = {} }) => {
    const canvasRef = useRef(null);
    const rafRef = useRef(null);
    const dataRef = useRef(null);

    const {
        sensitivity = 1.2,
        glowIntensity = 0.5,
        particleCount = 50,
        lineThickness = 2,
        mirrorMode = false,
    } = settings;

    const colors = COLOR_SCHEMES[colorScheme] || COLOR_SCHEMES.wave;

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const resize = () => {
            canvas.width = canvas.offsetWidth;
            canvas.height = canvas.offsetHeight;
        };
        resize();
        const ro = new ResizeObserver(resize);
        ro.observe(canvas);
        return () => ro.disconnect();
    }, []);

    const drawIdle = useCallback((ctx, W, H, time) => {
        const barCount = 48;
        const gap = 2;
        const barW = Math.max(2, (W - gap * (barCount - 1)) / barCount);
        ctx.fillStyle = `${colors[0]}44`;
        for (let i = 0; i < barCount; i++) {
            const v = (Math.sin(i * 0.38 + time * 0.0015) * 0.5 + 0.5) * 0.18 + 0.04;
            const barH = Math.max(2, v * H);
            ctx.fillRect(i * (barW + gap), H - barH, barW, barH);
        }
    }, [colors]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        let stopped = false;
        const s = { sensitivity, glowIntensity, particleCount, lineThickness, mirrorMode };

        const render = () => {
            if (stopped) return;
            const W = canvas.width, H = canvas.height;
            if (!W || !H) {
                rafRef.current = requestAnimationFrame(render);
                return;
            }
            const ctx = canvas.getContext('2d');
            const time = Date.now();
            ctx.clearRect(0, 0, W, H);

            if (!analyser || !isPlaying) {
                drawIdle(ctx, W, H, time);
                rafRef.current = requestAnimationFrame(render);
                return;
            }

            if (!dataRef.current || dataRef.current.size !== analyser.frequencyBinCount) {
                dataRef.current = {
                    freq: new Uint8Array(analyser.frequencyBinCount),
                    time: new Uint8Array(analyser.frequencyBinCount),
                    size: analyser.frequencyBinCount,
                };
            }
            analyser.getByteFrequencyData(dataRef.current.freq);
            analyser.getByteTimeDomainData(dataRef.current.time);

            const bg = ctx.createLinearGradient(0, 0, 0, H);
            bg.addColorStop(0, `${colors[0]}11`);
            bg.addColorStop(1, `${colors[1]}05`);
            ctx.fillStyle = bg;
            ctx.fillRect(0, 0, W, H);

            ctx.shadowBlur = s.glowIntensity > 0 ? 20 * s.glowIntensity : 0;
            ctx.shadowColor = colors[0];

            switch (style) {
                case 'wave': drawWave(ctx, W, H, dataRef.current.time, colors, s); break;
                case 'circular': drawCircular(ctx, W, H, dataRef.current.freq, colors, s); break;
                case 'particles': drawParticles(ctx, W, H, dataRef.current.freq, colors, s, time); break;
                case 'oscilloscope': drawOscilloscope(ctx, W, H, dataRef.current.time, colors, s); break;
                case 'spectrum': drawSpectrum(ctx, W, H, dataRef.current.freq, s); break;
                case 'dna': drawDNA(ctx, W, H, dataRef.current.freq, colors, s, time); break;
                default: drawBars(ctx, W, H, dataRef.current.freq, colors, s);
            }
            ctx.shadowBlur = 0;

            rafRef.current = requestAnimationFrame(render);
        };

        rafRef.current = requestAnimationFrame(render);
        return () => {
            stopped = true;
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
    }, [analyser, isPlaying, style, colors, sensitivity, glowIntensity, particleCount, lineThickness, mirrorMode, drawIdle]);

    return (
        <canvas
            ref={canvasRef}
            className="w-full h-full block"
            style={{ imageRendering: 'crisp-edges' }}
        />
    );
};

export default PlayerVisualizer;
